import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

const GenericImportModal = ({ isOpen, onClose, fields, onImport, title = "Importation de données" }) => {
    const [file, setFile] = useState(null);
    const [step, setStep] = useState(1); // 1: Select, 2: Map, 3: Preview
    const [fileData, setFileData] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [mapping, setMapping] = useState({});
    const [previewData, setPreviewData] = useState([]);
    const [isImporting, setIsImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);

    useEffect(() => {
        if (!isOpen) {
            setFile(null);
            setStep(1);
            setFileData([]);
            setHeaders([]);
            setMapping({});
            setPreviewData([]);
            setIsImporting(false);
            setImportResult(null);
        }
    }, [isOpen]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;
        setFile(selectedFile);

        const reader = new FileReader();
        const extension = selectedFile.name.split('.').pop().toLowerCase();

        reader.onload = (evt) => {
            let data = [];
            let currentHeaders = [];
            if (extension === 'csv') {
                const results = Papa.parse(evt.target.result, { header: true, skipEmptyLines: true });
                data = results.data;
                currentHeaders = results.meta.fields || [];
            } else {
                const workbook = XLSX.read(evt.target.result, { type: 'binary' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                data = XLSX.utils.sheet_to_json(worksheet);
                if (data.length > 0) {
                    currentHeaders = Object.keys(data[0]);
                }
            }
            setFileData(data);
            setHeaders(currentHeaders);

            // Auto-mapping suggestion
            const initialMapping = {};
            fields.forEach(field => {
                const match = currentHeaders.find(h =>
                    h.toLowerCase().includes(field.label.toLowerCase()) ||
                    h.toLowerCase().includes(field.key.toLowerCase())
                );
                if (match) initialMapping[field.key] = match;
            });
            setMapping(initialMapping);
            setStep(2);
        };

        if (extension === 'csv') {
            reader.readAsText(selectedFile);
        } else {
            reader.readAsBinaryString(selectedFile);
        }
    };

    const handleMappingChange = (fieldKey, headerName) => {
        setMapping(prev => ({ ...prev, [fieldKey]: headerName }));
    };

    const generatePreview = () => {
        const preview = fileData.slice(0, 5).map(row => {
            const mappedRow = {};
            fields.forEach(f => {
                mappedRow[f.key] = row[mapping[f.key]] || '';
            });
            return mappedRow;
        });
        setPreviewData(preview);
        setStep(3);
    };

    const handleFinalImport = async () => {
        const finalData = fileData.map(row => {
            const mappedRow = {};
            fields.forEach(f => {
                mappedRow[f.key] = row[mapping[f.key]] || '';
            });
            return mappedRow;
        });

        setIsImporting(true);
        try {
            const result = await onImport(finalData);
            setImportResult(result);
            setStep(4);
        } catch (error) {
            console.error("Import error:", error);
        } finally {
            setIsImporting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl shadow-primary/20 border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-500">

                {/* Premium Header with Gradient */}
                <div className="shrink-0 px-8 py-6 relative overflow-hidden bg-gradient-to-br from-primary/10 via-primary/5 to-transparent dark:from-primary/20 dark:via-primary/10 dark:to-transparent border-b border-primary/10 dark:border-primary/20">
                    <div className="relative z-10 flex justify-between items-center">
                        <div className="flex gap-4 items-center">
                            <div className="size-12 rounded-2xl bg-white dark:bg-slate-800 flex items-center justify-center shadow-sm border border-primary/10">
                                <span className="material-symbols-outlined text-primary text-[28px] font-bold">upload_file</span>
                            </div>
                            <div>
                                <h3 className="text-xl font-black text-slate-900 dark:text-white leading-tight">{title}</h3>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                                        {step === 4 ? "Opération terminée" : `Étape ${step} sur 3`}
                                    </span>
                                    <div className="flex gap-1">
                                        {[1, 2, 3].map(s => (
                                            <div key={s} className={`h-1 w-4 rounded-full transition-all ${step >= s ? 'bg-primary' : 'bg-slate-200 dark:bg-slate-700'}`}></div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="size-10 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-800 shadow-sm hover:shadow transition-all"
                        >
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-8 relative">

                    {/* Importing Overlay */}
                    {isImporting && (
                        <div className="absolute inset-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm flex flex-col items-center justify-center p-8 animate-in fade-in duration-300">
                            <div className="w-full max-w-md space-y-6 text-center">
                                <div className="size-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4 border border-primary/20">
                                    <div className="size-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                                </div>
                                <div>
                                    <p className="text-lg font-black text-slate-900 dark:text-white">Importation en cours...</p>
                                    <p className="text-sm text-slate-500 mt-1 font-medium">Traitement de {fileData.length} lignes en cours.</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 1: File Selection */}
                    {step === 1 && (
                        <div className="flex flex-col items-center justify-center h-80 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-[2rem] bg-slate-50/50 dark:bg-slate-800/30 hover:border-primary/50 transition-all group cursor-pointer relative overflow-hidden">
                            <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            <div className="text-center group-hover:scale-105 transition-transform relative z-10">
                                <div className="size-20 rounded-[2rem] bg-white dark:bg-slate-800 flex items-center justify-center mx-auto mb-6 shadow-sm border border-slate-100 dark:border-slate-700">
                                    <span className="material-symbols-outlined text-4xl text-primary">cloud_upload</span>
                                </div>
                                <p className="text-lg font-black text-slate-900 dark:text-white">Sélectionnez le fichier sources</p>
                                <p className="text-sm text-slate-400 mt-2 font-medium">Formats acceptés : .CSV, .XLSX, .XLS</p>
                            </div>
                            <input
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                onChange={handleFileChange}
                                className="absolute inset-0 opacity-0 cursor-pointer z-20"
                            />
                        </div>
                    )}

                    {/* Step 2: Mapping */}
                    {step === 2 && (
                        <div className="space-y-6 animate-in slide-in-from-bottom-2 duration-300">
                            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/50 p-5 rounded-2xl flex gap-4 text-amber-700 dark:text-amber-400">
                                <span className="material-symbols-outlined shrink-0 text-amber-500">lightbulb</span>
                                <p className="text-sm font-medium">Faites correspondre les colonnes de votre fichier avec les champs systèmes. Les champs marqués d'une étoile (<span className="text-rose-500">*</span>) sont requis.</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {fields.map(field => (
                                    <div key={field.key} className="p-5 bg-white dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center justify-between shadow-sm hover:shadow-md transition-all">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-black text-slate-900 dark:text-white">
                                                {field.label} {field.required && <span className="text-rose-500">*</span>}
                                            </span>
                                            <span className="text-[9px] text-slate-400 uppercase font-black tracking-widest">{field.key}</span>
                                        </div>
                                        <select
                                            value={mapping[field.key] || ''}
                                            onChange={(e) => handleMappingChange(field.key, e.target.value)}
                                            className="h-10 pl-4 pr-10 rounded-xl bg-slate-50 dark:bg-slate-900 border-2 border-transparent outline-none focus:border-primary/30 transition-all text-xs font-black text-slate-900 dark:text-white appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20stroke%3D%22%236b7280%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%20d%3D%22m6%208%204%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem_1.25rem] bg-[position:right_0.5rem_center] bg-no-repeat"
                                        >
                                            <option value="">(Ignorer)</option>
                                            {headers.map(h => (
                                                <option key={h} value={h}>{h}</option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Step 3: Preview */}
                    {step === 3 && (
                        <div className="space-y-6 animate-in slide-in-from-bottom-2 duration-300">
                            <div className="flex items-center gap-2">
                                <div className="w-1 h-4 bg-primary rounded-full"></div>
                                <h3 className="font-black text-xs uppercase tracking-widest text-slate-400">Aperçu de l'import (5 premiers éléments)</h3>
                            </div>
                            <div className="overflow-x-auto border border-slate-100 dark:border-slate-800 rounded-[2rem] bg-white dark:bg-slate-900/40">
                                <table className="w-full text-left text-xs">
                                    <thead>
                                        <tr className="bg-slate-50/50 dark:bg-slate-800/50">
                                            {fields.map(f => (
                                                <th key={f.key} className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 dark:border-slate-800">{f.label}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                                        {previewData.map((row, i) => (
                                            <tr key={i} className="hover:bg-slate-50/30 dark:hover:bg-slate-800/20 transition-colors">
                                                {fields.map(f => (
                                                    <td key={f.key} className="px-6 py-4 text-slate-900 dark:text-white font-bold">{row[f.key] || <span className="opacity-30 italic font-medium">-</span>}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Result Report */}
                    {step === 4 && importResult && (
                        <div className="flex flex-col items-center py-6 space-y-8 animate-in slide-in-from-bottom-4 duration-500">
                            <div className="size-24 bg-emerald-500/10 dark:bg-emerald-500/20 rounded-[2.5rem] flex items-center justify-center text-emerald-500 shadow-xl shadow-emerald-500/10 border border-emerald-500/20">
                                <span className="material-symbols-outlined text-4xl font-bold">check_circle</span>
                            </div>

                            <div className="text-center space-y-2">
                                <h4 className="text-2xl font-black text-slate-900 dark:text-white">Importation Terminée</h4>
                                <p className="text-sm text-slate-500 font-medium">Les données ont été synchronisées avec succès.</p>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full">
                                <div className="p-6 bg-slate-50/50 dark:bg-slate-800/30 rounded-[2rem] border border-slate-100 dark:border-slate-800/50 text-center">
                                    <span className="text-3xl font-black text-emerald-500 leading-none">{importResult.stats?.created || 0}</span>
                                    <p className="text-[9px] uppercase font-black tracking-[0.2em] text-slate-400 mt-2">Nouveaux</p>
                                </div>
                                <div className="p-6 bg-slate-50/50 dark:bg-slate-800/30 rounded-[2rem] border border-slate-100 dark:border-slate-800/50 text-center">
                                    <span className="text-3xl font-black text-primary leading-none">{importResult.stats?.updated || 0}</span>
                                    <p className="text-[9px] uppercase font-black tracking-[0.2em] text-slate-400 mt-2">MàJ</p>
                                </div>
                                <div className="p-6 bg-slate-50/50 dark:bg-slate-800/30 rounded-[2rem] border border-slate-100 dark:border-slate-800/50 text-center">
                                    <span className="text-3xl font-black text-rose-500 leading-none">{importResult.stats?.skipped || 0}</span>
                                    <p className="text-[9px] uppercase font-black tracking-[0.2em] text-slate-400 mt-2">Ignorés</p>
                                </div>
                                <div className="p-6 bg-slate-50/50 dark:bg-slate-800/30 rounded-[2rem] border border-slate-100 dark:border-slate-800/50 text-center">
                                    <span className="text-3xl font-black text-slate-700 dark:text-white leading-none">{importResult.stats?.total || 0}</span>
                                    <p className="text-[9px] uppercase font-black tracking-[0.2em] text-slate-400 mt-2">Fichiers</p>
                                </div>
                            </div>
                        </div>
                    )}

                </div>

                {/* Footer Selection */}
                <div className="shrink-0 p-8 border-t border-slate-100 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-800/40 flex justify-between items-center">
                    <div className="flex gap-3">
                        {step > 1 && step < 4 && (
                            <button
                                onClick={() => setStep(prev => prev - 1)}
                                className="h-12 px-6 rounded-2xl border border-slate-200 dark:border-slate-700 text-slate-500 font-bold hover:bg-white dark:hover:bg-slate-800 transition-all flex items-center gap-2"
                            >
                                <span className="material-symbols-outlined text-[20px]">west</span>
                                Précédent
                            </button>
                        )}
                        {step < 4 && (
                            <button onClick={onClose} className="h-12 px-6 rounded-2xl text-slate-400 font-black uppercase tracking-widest text-[10px] hover:text-slate-600 dark:hover:text-white transition-colors">
                                Annuler
                            </button>
                        )}
                    </div>

                    {step === 2 && (
                        <button
                            onClick={generatePreview}
                            className="h-12 px-10 bg-primary text-white text-[11px] font-black uppercase tracking-widest rounded-2xl shadow-xl shadow-primary/20 hover:bg-primary/90 transition-all flex items-center gap-2 transform active:scale-95"
                        >
                            <span>Analyser</span>
                            <span className="material-symbols-outlined text-[18px]">east</span>
                        </button>
                    )}

                    {step === 3 && (
                        <button
                            onClick={handleFinalImport}
                            disabled={isImporting}
                            className="h-12 px-10 bg-emerald-500 text-white text-[11px] font-black uppercase tracking-widest rounded-2xl shadow-xl shadow-emerald-500/20 hover:bg-emerald-600 transition-all flex items-center gap-2 disabled:opacity-50 transform active:scale-95"
                        >
                            <span className="material-symbols-outlined text-[18px]">rocket_launch</span>
                            Lancer l'importation
                        </button>
                    )}

                    {step === 4 && (
                        <button
                            onClick={onClose}
                            className="h-12 px-10 bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-[11px] font-black uppercase tracking-widest rounded-2xl shadow-xl hover:scale-102 transition-all"
                        >
                            Fermer
                        </button>
                    )}
                </div>

            </div>

            <style>{`
                @keyframes progress-loading {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(200%); }
                }
                .animate-progress-indeterminate {
                    width: 50%;
                    animation: progress-loading 1.5s infinite linear;
                }
            `}</style>
        </div>
    );
};

export default GenericImportModal;
